/**
 * @fileoverview Secure Logger Utility
 * @description Provides safe logging with sanitization to prevent log injection attacks
 * @module shared/secure-logger
 */

/**
 * Log Level Type
 * @description Defines available log severity levels
 */
export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

/**
 * Secure Logger
 * @class SecureLogger
 * @description Static logger utility available across the app without injection.
 * Provides safe logging with sanitization to prevent log injection attacks.
 * @example
 * ```typescript
 * SecureLogger.info('User logged in', { userId: '123' });
 * SecureLogger.error('API failed', error);
 * ```
 */
class SecureLogger {
  /** Maximum length for log messages before truncation */
  private static readonly MAX_LEN = 5000;

  private static readonly CRLF_PATTERN = /[\r\n]/g;
  private static readonly HTML_SIGNIFICANT_CHAR_PATTERN = /[<>&]/g;

  private constructor() {}

  private static sanitize(input: unknown): string {
    let s: string;

    if (input instanceof Error) {
      s = this.errorToString(input);
    } else if (typeof input === 'string') {
      s = input;
    } else {
      s = this.safeStringify(input);
    }

    // Strip ANSI escape sequences
    // eslint-disable-next-line no-control-regex
    s = s.replace(/\x1B\[[0-9;]*[a-zA-Z]/g, '');

    s = s.replace(/\r/g, '\\r').replace(/\n/g, '\\n').replace(/\t/g, '\\t');

    s = this.stripControlCharacters(s);

    // Neutralize Unicode line/paragraph separators
    s = s.replace(/[\u2028\u2029]/g, ' ');

    // Prevent console %-formatting tokens from doing odd things
    s = s.replace(/%[sdifoOj]/g, '%%');

    // Neutralize HTML-significant characters to avoid script-tag interpretation in HTML-rendered log sinks
    s = s.replace(SecureLogger.HTML_SIGNIFICANT_CHAR_PATTERN, (char) => {
      if (char === '<') return '\\u003C';
      if (char === '>') return '\\u003E';
      return '\\u0026';
    });

    s = s.replace(/ {2,}/g, ' ').trim();

    // Cap length to keep logs safe
    if (s.length > SecureLogger.MAX_LEN) {
      s = s.slice(0, SecureLogger.MAX_LEN) + '… [truncated]';
    }

    return s;
  }

  private static stripControlCharacters(value: string): string {
    return Array.from(value, (char) => {
      const code = char.charCodeAt(0);
      const isControlChar = (code >= 0 && code <= 8) || code === 11 || code === 12 || (code >= 14 && code <= 31) || code === 127;
      return isControlChar ? ' ' : char;
    }).join('');
  }

  private static safeStringify(obj: unknown): string {
    try {
      return JSON.stringify(obj);
    } catch {
      // Handle circular refs
      const seen = new WeakSet();
      return JSON.stringify(obj, (_k, v) => {
        if (typeof v === 'object' && v !== null) {
          if (seen.has(v)) return '[Circular]';
          seen.add(v);
        }
        return v;
      });
    }
  }

  private static errorToString(err: Error): string {
    const name = err.name || 'Error';
    const msg = err.message || 'Unknown error';
    return `${name}: ${msg}`;
  }

  /**
   * Format log message with timestamp and level
   * @param {LogLevel} level - Log severity level
   * @param {unknown[]} parts - Message parts to log
   * @returns {string} Formatted log message
   * @private
   */
  private static format(level: LogLevel, parts: unknown[]): string {
    const ts = new Date().toISOString();
    const payload = parts.map(p => this.sanitize(p)).join(' ');
    return `${ts} ${level} — ${payload}`;
  }

  private static sanitizeForLogSink(value: string): string {
    return value.replace(SecureLogger.CRLF_PATTERN, '');
  }

  private static write(level: LogLevel, output: string): void {
    const safeOutput = this.sanitizeForLogSink(output);

    if (level === 'ERROR') {
      console.error(safeOutput);
      return;
    }

    if (level === 'WARN') {
      console.warn(safeOutput);
      return;
    }

    if (level === 'INFO') {
      console.info(safeOutput);
      return;
    }

    console.debug(safeOutput);
  }

  /**
   * Log error message
   * @param {unknown} message - Error message or object
   * @param {unknown[]} optionalParams - Additional parameters to log
   */
  static error(message: unknown, ...optionalParams: unknown[]): void {
    const out = this.format('ERROR', [message, ...optionalParams]);
    this.write('ERROR', out);
  }

  /**
   * Log warning message
   * @param {unknown} message - Warning message or object
   * @param {unknown[]} optionalParams - Additional parameters to log
   */
  static warn(message: unknown, ...optionalParams: unknown[]): void {
    const out = this.format('WARN', [message, ...optionalParams]);
    this.write('WARN', out);
  }

  /**
   * Log info message
   * @param {unknown} message - Info message or object
   * @param {unknown[]} optionalParams - Additional parameters to log
   */
  static info(message: unknown, ...optionalParams: unknown[]): void {
    const out = this.format('INFO', [message, ...optionalParams]);
    this.write('INFO', out);
  }

  /**
   * Log debug message
   * @param {unknown} message - Debug message or object
   * @param {unknown[]} optionalParams - Additional parameters to log
   */
  static debug(message: unknown, ...optionalParams: unknown[]): void {
    const out = this.format('DEBUG', [message, ...optionalParams]);
    this.write('DEBUG', out);
  }
}

export { SecureLogger };
